import { ReactNode, createContext, useContext, useMemo, useState } from 'react'

export type Toast = { id: number; title?: string; description?: string; type?: 'success' | 'error' | 'info' }

type Ctx = {
  toasts: Toast[]
  push: (t: Omit<Toast, 'id'>) => void
  remove: (id: number) => void
}

const ToastCtx = createContext<Ctx | null>(null)
let inc = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const value = useMemo<Ctx>(() => ({
    toasts,
    push: (t) => {
      const toast = { id: inc++, ...t }
      setToasts((arr) => [...arr, toast])
      setTimeout(() => {
        setToasts((arr) => arr.filter((x) => x.id !== toast.id))
      }, 4000)
    },
    remove: (id) => setToasts((arr) => arr.filter((x) => x.id !== id))
  }), [toasts])
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="fixed bottom-4 right-4 flex flex-col gap-2 z-50">
        {toasts.map((t) => (
          <div key={t.id} className={`rounded shadow-md p-3 min-w-[240px] bg-white border ${bg(t.type)}`}>
            {t.title && <div className="font-semibold text-sm">{t.title}</div>}
            {t.description && <div className="text-xs text-muted">{t.description}</div>}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

function bg(type: Toast['type']) {
  switch (type) {
    case 'success':
      return 'border-green-600'
    case 'error':
      return 'border-red-600'
    default:
      return 'border-neutral-300'
  }
}

export function useToast() {
  const ctx = useContext(ToastCtx)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

